import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@cottage/design-system/internal-tooling/ui'

type PurchasingDuplicateProductDialogProps = {
  open: boolean
  proposedName?: string
  candidates: { id: number; name: string }[]
  busy?: boolean
  onClose: () => void
  onUseExisting: (productId: number) => void
  onCreateAnyway: () => void
}

export default function PurchasingDuplicateProductDialog({
  open,
  proposedName,
  candidates,
  busy = false,
  onClose,
  onUseExisting,
  onCreateAnyway,
}: PurchasingDuplicateProductDialogProps) {
  const [selectedId, setSelectedId] = useState(candidates[0]?.id ?? 0)

  useEffect(() => {
    const firstCandidate = candidates[0]
    if (open && firstCandidate) {
      setSelectedId(firstCandidate.id)
    }
  }, [open, candidates])

  if (!open) return null

  const nameLabel = proposedName ? `"${proposedName}"` : 'this product'

  return createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-lg">
        <h2 className="text-base font-semibold text-gray-900">Similar product already exists</h2>
        <p className="mt-2 text-sm text-gray-600">
          Creating {nameLabel} would duplicate an existing catalog item. Choose an existing product or
          create a new one anyway.
        </p>
        <ul className="mt-3 space-y-2">
          {candidates.map((candidate) => (
            <li key={candidate.id}>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-900">
                <input
                  type="radio"
                  name="duplicate-candidate"
                  checked={selectedId === candidate.id}
                  onChange={() => setSelectedId(candidate.id)}
                  disabled={busy}
                />
                {candidate.name}
              </label>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="outline" onClick={onCreateAnyway} disabled={busy}>
            Create anyway
          </Button>
          <Button variant="primary" onClick={() => onUseExisting(selectedId)} disabled={busy}>
            Use existing
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
